import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import api from '../../services/api';

const VillaDetail = () => {
  const { villaId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const { formatCurrency, settings } = useSettings();
  const [loading, setLoading] = useState(true);
  const [villa, setVilla] = useState(null);
  const [payments, setPayments] = useState([]);
  const [error, setError] = useState(null);
  const [ownerInfo, setOwnerInfo] = useState(null);
  
  // State for editing unit details (admin only)
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    size: '',
    bedrooms: '',
    bathrooms: '',
    built_year: ''
  });
  
  // Check if current user is admin
  const isAdmin = currentUser && (currentUser.role === 'admin' || currentUser.role === 'superadmin');
  
  // Custom styles based on theme settings
  const blockTitleStyle = {
    backgroundColor: settings.block_title_bg_color || '#f8f9fa',
    color: settings.block_title_color || '#8B4513'
  };

  useEffect(() => {
    const fetchVillaData = async () => {
      try {
        setLoading(true);
        console.log('Fetching villa details for ID:', villaId);
        
        // Fetch villa data
        const villaResponse = await api.get(`/api/villas/${villaId}`);
        console.log('Villa response:', villaResponse.data);
        
        // Check if this villa belongs to the current user (for owners) or allow access for admins
        if (currentUser.role === 'owner' && villaResponse.data.owner_id !== currentUser.id) {
          setError('You do not have permission to view this unit.');
          setLoading(false);
          return;
        }
        
        // Use API data directly for villa details
        const villaData = villaResponse.data;
        setVilla(villaData);
        
        // Initialize edit data
        setEditData({
          size: villaData.size || '',
          bedrooms: villaData.bedrooms || '',
          bathrooms: villaData.bathrooms || '',
          built_year: villaData.built_year || ''
        });
        
        // Fetch owner contact info
        if (villaData.owner_id) {
          try {
            const ownerRes = await api.get(`/api/users/${villaData.owner_id}`);
            setOwnerInfo(ownerRes.data);
          } catch (ownerErr) {
            console.error('Error fetching owner info:', ownerErr);
          }
        }
        
        // Fetch payment history for this villa
        try {
          const paymentsResponse = await api.get(`/api/financials/villa/${villaId}/history`);
          setPayments(paymentsResponse.data || []);
        } catch (paymentError) {
          console.error('Error fetching payments:', paymentError);
          // Set empty payments but don't block the whole page
          setPayments([]);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching villa details:', err);
        setError('Failed to load unit details. ' + (err.message || ''));
      } finally {
        setLoading(false);
      }
    };

    if (currentUser && villaId) {
      fetchVillaData();
    } else {
      navigate('/owner/my-units');
    }
  }, [villaId, currentUser, navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Handle saving unit details (admin only)
  const handleSaveUnitDetails = async () => {
    try {
      setLoading(true);
      
      // Update unit details via API
      await api.put(`/api/units/${villaId}`, {
        villa_number: villa.villa_number,
        unit_type: villa.unit_type,
        owner_id: villa.owner_id,
        contact_phone: villa.contact_phone,
        contact_email: villa.contact_email,
        maintenance_fee: villa.maintenance_fee,
        size: editData.size || null,
        bedrooms: editData.bedrooms ? parseInt(editData.bedrooms) : null,
        bathrooms: editData.bathrooms ? parseInt(editData.bathrooms) : null,
        built_year: editData.built_year ? parseInt(editData.built_year) : null
      });
      
      // Update local state
      setVilla(prev => ({
        ...prev,
        size: editData.size || null,
        bedrooms: editData.bedrooms ? parseInt(editData.bedrooms) : null,
        bathrooms: editData.bathrooms ? parseInt(editData.bathrooms) : null,
        built_year: editData.built_year ? parseInt(editData.built_year) : null
      }));
      
      setIsEditing(false);
      setError(null);
    } catch (err) {
      console.error('Error updating unit details:', err);
      setError('Failed to update unit details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle canceling edit
  const handleCancelEdit = () => {
    setEditData({
      size: villa.size || '',
      bedrooms: villa.bedrooms || '',
      bathrooms: villa.bathrooms || '',
      built_year: villa.built_year || ''
    });
    setIsEditing(false);
  };

  // Generate unit type prefix for ID and Number
  const getUnitPrefix = () => {
    const unitType = villa.unit_type || 'Unit';
    return unitType;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center mt-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </div>
        <div className="text-center mt-3">
          <Link to="/owner/my-units" className="btn btn-primary">
            <i className="fas fa-arrow-left me-2"></i> Back to My Units
          </Link>
        </div>
      </div>
    );
  }

  if (!villa) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Unit not found or you don&apos;t have permission to view it.
        </div>
        <div className="text-center mt-3">
          <Link to="/owner/my-units" className="btn btn-primary">
            <i className="fas fa-arrow-left me-2"></i> Back to My Units
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{getUnitPrefix()} {villa.villa_number} Details</h2>
        <Link to="/owner/my-units" className="btn btn-outline-primary">
          <i className="fas fa-arrow-left me-2"></i> Back to My Units
        </Link>
      </div>

      <div className="row">
        {/* Main Unit Info */}
        <div className="col-lg-12 col-md-12 mb-4">
          <div className="card shadow">
            <div className="card-header" style={blockTitleStyle}>
              <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-home me-2"></i>
                  Unit Information
              </h5>
                {isAdmin && (
                  <div>
                    {!isEditing ? (
                      <button 
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setIsEditing(true)}
                      >
                        <i className="fas fa-edit me-1"></i> Edit Details
                      </button>
                    ) : (
                      <div className="btn-group">
                        <button 
                          className="btn btn-sm btn-success"
                          onClick={handleSaveUnitDetails}
                          disabled={loading}
                        >
                          <i className="fas fa-save me-1"></i> Save
                        </button>
                        <button 
                          className="btn btn-sm btn-secondary"
                          onClick={handleCancelEdit}
                        >
                          <i className="fas fa-times me-1"></i> Cancel
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="card-body">
              <div className="row mb-4">
                <div className="col-md-6">
                  <h6><i className="fas fa-id-card me-2"></i> {getUnitPrefix()} ID</h6>
                  <p className="text-muted">#{villa.id}</p>
                </div>
                <div className="col-md-6">
                  <h6><i className="fas fa-hashtag me-2"></i> {getUnitPrefix()} Number</h6>
                  <p className="text-muted">{getUnitPrefix()} #{villa.villa_number}</p>
                </div>
              </div>
              
              <div className="row mb-4">
                <div className="col-md-12">
                  <h6><i className="fas fa-map-marker-alt me-2"></i> Address</h6>
                  <p className="text-muted">{villa.address || settings.society_address || 'N/A'}</p>
                </div>
              </div>
              
              <div className="row mb-4">
                <div className="col-md-4">
                  <h6><i className="fas fa-expand-arrows-alt me-2"></i> Size</h6>
                  {isEditing ? (
                    <div className="input-group input-group-sm">
                      <input
                        type="number"
                        className="form-control"
                        value={editData.size ? editData.size.replace(' sq ft', '').replace('sq ft', '').trim() : ''}
                        onChange={(e) => setEditData({...editData, size: e.target.value ? `${e.target.value} sq ft` : ''})}
                        placeholder="1200"
                        min="0"
                      />
                      <span className="input-group-text">sq ft</span>
                    </div>
                  ) : (
                    <p className="text-muted">{villa.size || 'N/A'}</p>
                  )}
                </div>
                <div className="col-md-4">
                  <h6><i className="fas fa-bed me-2"></i> Bedrooms</h6>
                  {isEditing ? (
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={editData.bedrooms}
                      onChange={(e) => setEditData({...editData, bedrooms: e.target.value})}
                      placeholder="e.g. 3"
                      min="0"
                    />
                  ) : (
                    <p className="text-muted">{villa.bedrooms || 'N/A'}</p>
                  )}
                </div>
                <div className="col-md-4">
                  <h6><i className="fas fa-bath me-2"></i> Bathrooms</h6>
                  {isEditing ? (
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={editData.bathrooms}
                      onChange={(e) => setEditData({...editData, bathrooms: e.target.value})}
                      placeholder="e.g. 2"
                      min="0"
                    />
                  ) : (
                    <p className="text-muted">{villa.bathrooms || 'N/A'}</p>
                  )}
                </div>
              </div>
              
              <div className="row mb-4">
                <div className="col-md-6">
                  <h6><i className="fas fa-calendar-alt me-2"></i> Built Year</h6>
                  {isEditing ? (
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={editData.built_year}
                      onChange={(e) => setEditData({...editData, built_year: e.target.value})}
                      placeholder="e.g. 2020"
                      min="1900"
                      max={new Date().getFullYear()}
                    />
                  ) : (
                    <p className="text-muted">{villa.built_year || 'N/A'}</p>
                  )}
                </div>
              </div>
              
              <div className="alert alert-info d-block">
                <div className="d-flex flex-wrap justify-content-between align-items-center">
                  <div className='mb-2'>
                    <strong>Monthly Maintenance Fee:</strong>
                    <div className="fs-4">{formatCurrency(villa.maintenance_fee)}</div>
                  </div>
                  <Link to="/owner/payments" className="btn btn-primary">
                    <i className="fas fa-money-bill-wave me-2"></i> Payment History
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Recent Payments */}
        <div className="villaDetailsPayments d-flex col-lg-12 col-md-12 mb-4">
          <div className="card shadow w-50 m-2 villaDetailsCard">
            <div className="card-header" style={blockTitleStyle}>
              <h5 className="mb-0">
                <i className="fas fa-money-check-alt me-2"></i>
                Recent Payments
              </h5>
            </div>
            <div className="card-body">
              {payments.length > 0 ? (
                <div className="list-group">
                  {payments.slice(0, 5).map(payment => (
                    <div key={payment.id} className="list-group-item">
                      <div className="d-flex justify-content-between">
                        <div>
                          <div>{formatDate(payment.date)}</div>
                          <small className="text-muted">{payment.type || 'Maintenance'}</small>
                        </div>
                        <div className="fw-bold">
                          {formatCurrency(payment.amount)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="alert alert-light">
                  <i className="fas fa-info-circle me-2"></i>
                  No recent payment records found.
                </div>
              )}
              
              <div className="d-grid mt-3">
                <Link to="/owner/payments" className="btn btn-outline-success">
                  <i className="fas fa-history me-2"></i> View All Payments
                </Link>
              </div>
            </div>
          </div>
          
          {/* Contact Card */}
          <div className="card shadow w-50 m-2 villaDetailsCard">
            <div className="card-header" style={blockTitleStyle}>
              <h5 className="mb-0">
                <i className="fas fa-address-card me-2"></i>
                Contact Information
              </h5>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <h6><i className="fas fa-phone me-2"></i> Phone</h6>
                <p className="text-muted">{ownerInfo?.phone || 'Not Available'}</p>
              </div>
              <div className="mb-3">
                <h6><i className="fas fa-envelope me-2"></i> Email</h6>
                <p className="text-muted">{ownerInfo?.email || 'Not Available'}</p>
              </div>
              <div className="alert alert-light mb-0">
                <small>
                  <i className="fas fa-info-circle me-2"></i>
                  For emergencies, please contact the society management office.
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VillaDetail; 